import { AxiosResponse } from 'axios';
import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  MonitoringGridViewProtocolCancelConfirmRequest,
  MonitoringGridViewProtocolGetDetailRequest,
  MonitoringGridViewProtocolGetDetailResponse,
  MonitoringGridViewProtocolGetListResponse,
  MonitoringMonitoringProtocolGetChannelGroupCountResponse,
  MonitoringMonitoringProtocolGetListingCountResponse,
  MonitoringMonitoringProtocolGetMonitoringCountRequest,
  MonitoringMonitoringProtocolGetMonitoringListRequest,
  MonitoringMonitoringProtocolGetStatusCountRequest,
  MonitoringMonitoringProtocolGetStatusCountResponse,
  MonitoringMonitoringProtocolGetWebsiteFilterRequest,
  MonitoringTableViewProtocolGetListResponse,
} from '~/swagger/data-contracts';
import { queryClient } from '~/index';
import { monitoringApi } from './';

export const QueryKeyMonitoring = 'monitoring';
const queryOptions = {
  refetchOnWindowFocus: false,
};

// filter

export const useGetWebsiteProfileFilterList = (request: MonitoringMonitoringProtocolGetWebsiteFilterRequest) => {
  return useInfiniteQuery(
    [QueryKeyMonitoring, 'filter', 'website_profile', request],
    async ({ pageParam }) => {
      const { data } = await monitoringApi.filterWebsiteCreate({
        ...request,
        offset: pageParam || request.offset,
      });
      return data;
    },
    {
      ...queryOptions,
      getNextPageParam: (lastPage, allPage) => {
        if (lastPage.websites.length < request.limit) {
          return;
        }
        return request.limit * allPage.length;
      },
    }
  );
};

// count

export const useGetChannelGroupCount = (): UseQueryResult<MonitoringMonitoringProtocolGetChannelGroupCountResponse> => {
  return useQuery(
    [QueryKeyMonitoring, 'get_channel_group_count'],
    async () => {
      const { data } = await monitoringApi.channelGroupCountList();
      return data;
    },
    queryOptions
  );
};

export const useGetStatusCount = (
  request: MonitoringMonitoringProtocolGetStatusCountRequest
): UseQueryResult<MonitoringMonitoringProtocolGetStatusCountResponse> => {
  return useQuery(
    [QueryKeyMonitoring, 'get_status_count', request],
    async () => {
      const { data } = await monitoringApi.statusCountCreate(request);
      return data;
    },
    queryOptions
  );
};

export const useGetListingCount = (
  request: MonitoringMonitoringProtocolGetMonitoringCountRequest
): UseQueryResult<MonitoringMonitoringProtocolGetListingCountResponse> => {
  return useQuery(
    [QueryKeyMonitoring, 'get_listing_count', request],
    async () => {
      const { data } = await monitoringApi.listingCountCreate(request);
      return data;
    },
    queryOptions
  );
};

/**
 * table view
 */
export const useGetTableListings = (
  request: MonitoringMonitoringProtocolGetMonitoringListRequest
): UseQueryResult<MonitoringTableViewProtocolGetListResponse> => {
  return useQuery(
    [QueryKeyMonitoring, 'get_table_list', request],
    async () => {
      const { data } = await monitoringApi.tableListCreate(request);
      return data;
    },
    queryOptions
  );
};

export const useTableConfirmMutation = () => {
  return useMutation(monitoringApi.tableConfirmCreate, {
    onSuccess: (_) => {
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_table_list']);
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_status_count']);
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_listing_count']);
    },
  });
};

export const useTableCancelConfirmMutation = () => {
  return useMutation(monitoringApi.tableCancelConfirmCreate, {
    onSuccess: (_) => {
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_table_list']);
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_status_count']);
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_listing_count']);
    },
  });
};

export const useTableDismissMutation = () => {
  return useMutation(monitoringApi.tableDismissCreate, {
    onSuccess: (_) => {
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_table_list']);
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_status_count']);
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_listing_count']);
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_channel_group_count']);
    },
  });
};

/**
 * grid view
 */
export const useGetGridListings = (
  request: MonitoringMonitoringProtocolGetMonitoringListRequest
): UseInfiniteQueryResult<MonitoringGridViewProtocolGetListResponse> => {
  return useInfiniteQuery(
    [QueryKeyMonitoring, 'get_grid_list', request],
    async ({ pageParam }) => {
      const { data } = await monitoringApi.gridListCreate({ ...request, offset: pageParam || request.offset });
      return data;
    },
    {
      ...queryOptions,
      getNextPageParam: (lastPage, allPage) => {
        if (lastPage.list.length < request.limit) {
          return;
        }

        return request.limit * allPage.length;
      },
    }
  );
};

export const useGetListingDetail = (
  request: MonitoringGridViewProtocolGetDetailRequest
): UseQueryResult<MonitoringGridViewProtocolGetDetailResponse> => {
  return useQuery(
    [QueryKeyMonitoring, 'get_listing_detail', request],
    async () => {
      const { data } = await monitoringApi.gridDetailCreate(request);
      return data;
    },
    queryOptions
  );
};

export const useGridConfirmMutation = () => {
  return useMutation(monitoringApi.gridConfirmCreate, {
    onSuccess: (_) => {
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_grid_list']);
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_status_count']);
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_listing_count']);
    },
  });
};

export const useGetGridNoteDocumentFileUrlMutation = () => {
  return useMutation(monitoringApi.gridNotePresignedCreate);
};

export const useGridCancelConfirmMutation = (): UseMutationResult<
  AxiosResponse<void>,
  unknown,
  MonitoringGridViewProtocolCancelConfirmRequest,
  unknown
> => {
  return useMutation(monitoringApi.gridCancelConfirmCreate, {
    onSuccess: (_) => {
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_grid_list']);
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_status_count']);
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_listing_count']);
    },
  });
};

export const useGridDismissMutation = () => {
  return useMutation(monitoringApi.gridDismissCreate, {
    onSuccess: (_) => {
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_grid_list']);
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_status_count']);
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_listing_count']);
      queryClient.invalidateQueries([QueryKeyMonitoring, 'get_channel_group_count']);
    },
  });
};
